import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, ArrowRight, Lock, ScanLine, FileCheck } from 'lucide-react';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LocalizedLink } from '@/components/LocalizedLink';

export function ServiceSecurity() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const offerings = [
    {
      icon: ScanLine,
      title: t('services.security.offering1.title'),
      description: t('services.security.offering1.description'),
    },
    {
      icon: Lock,
      title: t('services.security.offering2.title'),
      description: t('services.security.offering2.description'),
    },
    {
      icon: FileCheck,
      title: t('services.security.offering3.title'),
      description: t('services.security.offering3.description'),
    },
  ];

  const services = t('services.security.services', { returnObjects: true }) as string[];

  return (
    <>
      <Helmet>
        <title>{t('services.security.pageTitle')}</title>
        <meta name="description" content={t('services.security.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/cybersecurity`} />
      </Helmet>

      {/* Hero */}
      <Section className="bg-gradient-to-br from-primary-50 to-white" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-5">
              <span className="inline-block px-3 py-1.5 bg-primary-100 text-primary-600 rounded-full text-sm font-medium">
                {t('services.security.badge')}
              </span>
              <h1 className="heading-1">
                <span className="text-primary-400">{t('services.security.heroTitle')}</span>
              </h1>
              <p className="body-large">
                {t('services.security.heroDescription')}
              </p>
              <div className="flex flex-wrap gap-3">
                <LocalizedLink to="contacts">
                  <Button variant="primary" size="lg">
                    {t('services.security.ctaButton')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </LocalizedLink>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <Shield className="w-16 h-16 mx-auto mb-4 opacity-90" />
                  <div className="text-2xl font-bold">{t('services.security.heroTitle')}</div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Offerings */}
      <Section padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="caption text-primary-400">{t('services.security.offeringsTitle')}</span>
            <h2 className="heading-2 mt-3">{t('services.security.offeringsSubtitle')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {offerings.map((item) => (
              <Card key={item.title} variant="hover">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-primary-400" />
                </div>
                <h3 className="heading-4 mb-2">{item.title}</h3>
                <p className="body-small">{item.description}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      {/* Services */}
      <Section variant="secondary" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <span className="caption text-primary-400">{t('services.security.servicesTitle')}</span>
              <h2 className="heading-2 mt-3 mb-4">{t('services.security.servicesSubtitle')}</h2>
              <p className="body-regular">
                {t('services.security.servicesDescription')}
              </p>
            </div>
            <div className="space-y-3">
              {services.map((service) => (
                <div key={service} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-400 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
